/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/05/22
 */

import { FormType } from '@/components/contexts/FormsContext/FormsProvider';
import { Labels, LabelsContext } from '@/components/contexts/LabelsContext/LabelsProvider';
import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import { useContext, useMemo } from 'react';

const useLatestFacultyLabels = <K extends keyof Labels, P extends keyof Labels[K]>(
    form: K,
    property: P,
): Labels[K][P] => {
    const { getGenericLabels } = useContext(LabelsContext);
    return getGenericLabels()[form][property];
};

export const getWorkplanFormIdForLabelFormType = (type: keyof Labels, workplan: Workplan) => {
    switch (type) {
        default:
        case FormType.ACTIVITY:
            return workplan.activityFormTemplateId;
        case FormType.PERSONAL_GOAL:
            return workplan.goalFormTemplateId;
        case FormType.LEAVE:
            return workplan.leaveFormTemplateId;
        case FormType.CONSIDERATION:
            return workplan.considerationFormTemplateId;
        case FormType.BEHAVIOURAL:
            return workplan.behaviouralFormTemplateId;
    }
};

export const useWorkplanLabels = <K extends keyof Labels, P extends keyof Labels[K]>(
    workplan: Workplan | undefined,
    form: K,
    property: P,
): Labels[K][P] => {
    const { getLabelsForFormId, getGenericLabels } = useContext(LabelsContext);

    return useMemo(() => {
        if (!workplan) {
            return getGenericLabels()[form][property];
        }
        const formId = getWorkplanFormIdForLabelFormType(form, workplan);
        return {
            ...getGenericLabels()[form][property],
            ...getLabelsForFormId(formId, form)[property as string],
        } as Labels[K][P];
    }, [getLabelsForFormId, workplan, form, property]);
};

export default useLatestFacultyLabels;
